<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("anexos_empresa_servicios.grid.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearAnexo()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-operaciones">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Nombre" :caption="$t('anexos_empresa_servicios.grid.nombre')" data-type="string" />
      <DxColumn data-field="Descripcion" :caption="$t('anexos_empresa_servicios.grid.descripcion')" data-type="string" />
      <DxColumn data-field="NumVariables" :caption="$t('anexos_empresa_servicios.grid.num_variables')"
        data-type="number" :width="130" :allow-filtering="false" />
      <DxColumn data-field="FechaAlta" :caption="$t('anexos_empresa_servicios.grid.fecha_alta')"
        data-type="date" format="dd/MM/yyyy" />
      <DxColumn data-field="FechaUltModificacion" :caption="$t('anexos_empresa_servicios.grid.fecha_ult_modificacion')"
        data-type="date" format="dd/MM/yyyy" />
      <DxColumn type="buttons" :caption="$t('anexos_empresa_servicios.grid.acciones')"
        :fixed="true" fixed-position="right"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('anexos_empresa_servicios.grid.modificar'),
            icon: 'fa-regular fa-edit text-success',
            onClick: (e: any) => verDetalle(e.row?.data.Id)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('anexos_empresa_servicios.grid.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row?.data.Id)
          }
        ]"
      />
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { type DXLoadOptions } from "@api/api";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { useRouter } from "@services/routing-services";

const intl = useIntl();
const api = useApi();
const router = useRouter();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();

const props = defineProps({
  idEmpresaServicios: {
    type: String,
    required: true
  }
});

onMounted(() => {
  setDatasource();
});

function setDatasource() {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getAnexosEmpresaServiciosForGrid", { showLoading: false }, props.idEmpresaServicios, q);
    }
  });
}

function abrirVistaAnexo(query?: { id: string }) {
  const rawTipo = router.currentRoute.value.params.tipoServicio;
  const tipoServicio = (Array.isArray(rawTipo) ? rawTipo[0] : rawTipo) as string;
  const resolved = router.resolve({
    name: "empresa-servicios-anexo",
    params: {
      tipoServicio,
      idEmpresaServicios: props.idEmpresaServicios,
    },
    query: query?.id ? { id: query.id } : {},
  });
  window.open(resolved.href, "_blank");
}

function crearAnexo() {
  abrirVistaAnexo();
}

function verDetalle(id: string) {
  abrirVistaAnexo({ id });
}

async function borrar(id: string) {
  const respuesta = await modal.messageBox(
    intl.i18n.t("anexos_empresa_servicios.grid.borrar_pregunta"),
    intl.i18n.t("anexos_empresa_servicios.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning"
  );
  if (respuesta === messageBoxResult.ok) {
    await api.callStandardApi("deleteAnexoEmpresaServicios", { showLoading: true }, id)
      .then(() => {
        toastSuccess(intl.i18n.t("anexos_empresa_servicios.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("anexos_empresa_servicios.borrado_fallo")));
  }
}
</script>

<style lang="scss" scoped></style>
